import React, { useMemo, type ReactNode } from 'react';
import { generatePath, useParams } from 'react-router-dom';
import { pipe, Option } from 'effect';
import { mapAccessInput, type AccessInput } from '@/abilities/accessInput';
import { landingDestination, type DestinationAccess, type DestinationTree } from '@/abilities/destination';
import type { RouteCondition } from '@/abilities/routeCondition';
import ROUTES_PATH, { type RouteKey } from '@/router/routesPathConfig';
import RouteGate from '@/router/RouteGate';
import type { RouteLanding } from '@/router/FirstOpenChildRedirect';

/**
 * Builds an area's route helper. A destination and its address are two names; the area links them once, in its map,
 * and its routes name only the destination: the helper takes the route's id and path from the map, puts the
 * destination in `handle` and wraps the page in the guard asking that destination, so none of them can disagree.
 */
export const createDestinationRoute =
  <Key extends string, Destination>(
    addressOf: Record<Key, RouteKey>,
    toDestination: (destination: Key) => Destination,
    canOpen: (destination: Key) => RouteCondition,
  ) =>
  (destination: Key, page: ReactNode) => {
    const route = ROUTES_PATH[addressOf[destination]];
    return {
      id: route.id,
      path: route.relative,
      handle: toDestination(destination),
      // Do not drop the key: a condition is a hook, and a gate React reuses for a sibling route would call that
      // route's other hooks in the same component and crash.
      element: (
        <RouteGate key={route.id} canOpen={canOpen(destination)}>
          {page}
        </RouteGate>
      ),
    };
  };

/**
 * Builds an area's landing from its tree, its map and its access reader: where a destination leads — itself when it is an
 * open leaf, otherwise its first open child in the tree's order, followed down to a leaf; none when nothing under it
 * is open.
 */
export const createDestinationLanding = <Key extends string>(
  tree: DestinationTree<Key>,
  addressOf: Record<Key, RouteKey>,
  useAccess: () => AccessInput<{ readonly accessOf: (destination: Key) => DestinationAccess }>,
) => {
  const landingPath = (
    accessOf: (destination: Key) => DestinationAccess,
    destination: Key,
    companyId: string | undefined,
  ): Option.Option<string> =>
    pipe(
      landingDestination(accessOf, tree)(destination),
      Option.map((key) => generatePath(ROUTES_PATH[addressOf[key]].absolute, { companyId })),
    );

  // For `FirstOpenChildRedirect`; created once, with the route table.
  const landing = (container: Key): RouteLanding =>
    function useDestinationLanding() {
      const access = useAccess();
      const { companyId } = useParams();
      return useMemo(
        () => mapAccessInput(access, ({ accessOf }) => landingPath(accessOf, container, companyId)),
        [access, companyId],
      );
    };

  return { landingPath, landing };
};
